import { Component, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core';
import {
  Question,
  QuestionType,
} from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';
import { DynamicService } from 'src/app/core/services/base/dynamic.service';
import { UserService } from 'src/app/core/services/user.service';
import { QuestionnaireAnswersStateService } from 'src/app/core/services/questionnaire-answers-state.service';
import { KycFieldLockService } from 'src/app/core/services/kyc-field-lock.service';
import { isPlausibleNamePart } from 'src/app/core/utils/legal-name.util';

/** CPK-5328: perguntas cuja resposta vira o nome do cliente (fillInfo → cp_pii). */
const PROFILE_NAME_FIELDS = ['attributes.first_name', 'attributes.surname', 'legal_name'];

export interface AutofillFilter {
  op: string;
  col: string;
  val: string;
}

@Component({
  selector: 'app-question-text',
  templateUrl: './question-text.component.html',
  styleUrls: ['./question-text.component.scss'],
})
export class QuestionTextComponent implements OnInit {
  @Input() question!: Question;
  @Input() user: any;
  @Output() changeAnswer = new EventEmitter<QuestionAnswer>();

  isLongText: boolean = false;
  autofillMode: boolean = false;
  autofillEditable: boolean = false;
  kycLocked: boolean = false;
  userId: string = '';
  regexPattern: string = '';
  inputMode: string = 'text';
  autocomplete: string | null = null;
  isProfileNameField: boolean = false;

  private readonly validInputModes = ['none', 'text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url'];

  constructor(
    private dynamicService: DynamicService<any>,
    private userService: UserService,
    private kycFieldLock: KycFieldLockService,
    @Optional() private answersState: QuestionnaireAnswersStateService | null
  ) {}

  async ngOnInit() {
    this.initializeQuestion();
  }

  private async initializeQuestion() {
    this.determineQuestionType();
    await this.handleConfiguration();
    this.validateAndEmit();
  }

  private determineQuestionType() {
    this.isLongText = this.question.response_type === QuestionType.LONG_TEXT;
  }

  private async handleConfiguration() {
    if (this.question.configuration) {
      const locked = await this.applyKycLock();
      if (
        !locked &&
        this.question.configuration.autofill &&
        !this.question.answer?.value
      ) {
        await this.processAutofill(this.question.configuration.autofill);
      }
      if (this.question.configuration.validate) {
        this.processValidationConfig(this.question.configuration.validate);
      }
      const configInputMode = this.question.configuration.inputmode;
      if (configInputMode && this.validInputModes.includes(configInputMode)) {
        this.inputMode = configInputMode;
      }
      const configAutocomplete = this.question.configuration.autocomplete;
      if (configAutocomplete) {
        this.autocomplete = configAutocomplete;
      }
      const fillInfo = this.question.configuration.fillInfo;
      this.isProfileNameField =
        fillInfo?.target === 'profile' && PROFILE_NAME_FIELDS.includes(fillInfo?.field);
    }
  }

  private async processAutofill(autofillConfig: any) {
    this.autofillMode = true;
    this.autofillEditable = autofillConfig.editable ?? false;
    await this.fetchCurrentUserId();

    const filters = this.replacePlaceholdersInFilters(autofillConfig.filters);
    const autofillValue = await this.fetchAutofillValue(
      autofillConfig.from,
      autofillConfig.select,
      filters
    );

    if (autofillValue) {
      this.question.answer = { value: autofillValue };
    }
  }

  /**
   * CPK-5059: com fillInfo.lockWhenKycPassed e KYC aprovado, mostra o valor do cadastro
   * (mesmo sobre um rascunho) e trava o campo. Vence o autofill.editable.
   */
  private async applyKycLock(): Promise<boolean> {
    if (!KycFieldLockService.lockedField(this.question)) return false;
    await this.fetchCurrentUserId();

    const lock = await this.kycFieldLock.resolve(this.question, this.userId);
    if (!lock.locked) return false;

    this.kycLocked = true;
    this.question.answer = { ...this.question.answer, value: lock.value };
    return true;
  }

  private async fetchCurrentUserId() {
    if (this.user) {
      this.userId = this.user.id;
      return;
    }
    const currentUser = await this.userService.getCurrentUser();
    this.userId = currentUser?.id || '';
  }

  private replacePlaceholdersInFilters(
    filters: AutofillFilter[]
  ): AutofillFilter[] {
    return filters.map((filter) => ({
      ...filter,
      val: filter.val.replace('$userId', this.userId),
    }));
  }

  private async fetchAutofillValue(
    from: string,
    select: string,
    filters: AutofillFilter[]
  ): Promise<string | null> {
    try {
      this.dynamicService.load(from);
      const result = await this.dynamicService.autofillQuery(
        from,
        select,
        filters
      );

      const splitParts = (select as any).split('->>');
      const lastPart = splitParts[splitParts.length - 1];
      const cleanedLastPart = lastPart.replace(/['"]/g, ''); // Remove aspas

      // Para campos JSONB (ex: attributes->>'first_name'),
      // o Supabase retorna com a chave sendo a última parte após ->>
      // Tentar com a última parte (com ou sem aspas)
      return (
        result?.[0]?.[cleanedLastPart] ||
        result?.[0]?.[lastPart] ||
        result?.[0]?.[select as any] ||
        null
      );
    } catch (error) {
      console.error('Error fetching autofill value:', error);
      return null;
    }
  }

  private processValidationConfig(validateConfig: any) {
    if (validateConfig.regex) {
      if (typeof validateConfig.regex === 'string') {
        this.regexPattern = validateConfig.regex;
      } else {
        this.regexPattern = validateConfig.regex.value;
      }
    }
  }

  onBlur(): void {
    if (this.question?.slug) {
      this.answersState?.blurSlug(this.question.slug);
    }
  }

  /**
   * Realiza a validação da resposta e emite o evento de mudança.
   */
  validateAndEmit() {
    this.validateAnswer();
    this.emitChange();
  }

  private validateAnswer() {
    // Valor travado veio do cadastro com KYC aprovado: a regex do questionário não pode prender o cliente.
    if (this.kycLocked) {
      this.resetValidation();
      return;
    }

    // Verificar se é obrigatório e está vazio
    if (this.question.elective === false && !this.hasValue()) {
      this.setValidationError('is required.');
      return;
    }

    if (this.hasValue()) {
      this.performValidations();
    } else {
      this.resetValidation();
    }
  }

  private resetValidation() {
    this.question.isValid = true;
    this.question.messageError = '';
  }

  private hasValue(): boolean {
    const value = this.question.answer?.value;
    return (
      value !== null && value !== undefined && value.toString().trim() !== ''
    );
  }

  private performValidations() {
    if (this.regexPattern && !this.validateRegex()) {
      return;
    }

    if (this.isProfileNameField && !this.validateNamePart()) {
      return;
    }

    // Every rule passed (or there are none) — having a non-empty value is
    // sufficient. Without this reset, an earlier "is required" error persists
    // even after the user fills the field.
    this.resetValidationOnSuccess();
  }

  /** false when the value fails the regex (error set) or is not a string (state kept). */
  private validateRegex(): boolean {
    const regex = new RegExp(this.regexPattern);
    const value = this.question.answer?.value;

    if (typeof value !== 'string') {
      return false;
    }

    if (!regex.test(value)) {
      const errorMessage =
        this.question.configuration?.validate?.regex?.messageError ||
        'The system only supports English characters.';
      this.setValidationError(errorMessage);
      return false;
    }
    return true;
  }

  /**
   * CPK-5328: a pergunta de nome não aceita palavra de documento ("Portador", "Nome", "Pai") —
   * a regex do banco só barra dígitos e símbolos.
   */
  private validateNamePart(): boolean {
    const value = this.question.answer?.value;
    if (typeof value === 'string' && !isPlausibleNamePart(value)) {
      this.setValidationError('NameMustNotContainDocumentLabel');
      return false;
    }
    return true;
  }

  private resetValidationOnSuccess() {
    this.question.isValid = true;
    this.question.messageError = '';
  }

  private setValidationError(message: string) {
    this.question.isValid = false;
    this.question.messageError = message;
  }

  private emitChange() {
    this.changeAnswer.emit({
      answer: this.question.answer!,
      question: this.question,
    });
  }
}
